import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { toast } from '../components/Toast'
import Spinner from '../components/Spinner'

// ─── 타입 ───────────────────────────────────────────────────────────────────

type Summary = {
  total_calls: number
  today_calls: number
  last_7d_calls: number
  last_30d_calls: number
  total_openai_tokens: number
  last_30d_openai_tokens: number
}

type FeatureRow = { feature: string; count: number }
type FeaturesResp = { days: number; features: FeatureRow[] }

type UserRow = { user_id: number; count: number }
type UsersResp = { days: number; users: UserRow[] }

type DailyRow = { date: string; count: number }
type DailyResp = { days: number; daily: DailyRow[] }

// ─── 한국어 feature 레이블 ──────────────────────────────────────────────────

const FEATURE_LABEL: Record<string, string> = {
  counseling_chat: '상담 채팅',
  counseling_qna: '상담 Q&A',
  lucky_today: '오늘의 행운',
  compatibility: '궁합',
  group_compatibility: '그룹 궁합',
  character_card: '캐릭터 카드',
  celebrity_match: '셀럽 매치',
  job_recommend: '직업 추천',
  daeun: '대운 분석',
  lucky_date: '택일',
  dream_interpretation: '꿈 해몽',
  yearly_report: '신년운세',
  name_analysis: '이름 풀이',
  personality_summary: '성격 요약',
  card_compatibility: '카드 궁합',
}

function featureLabel(code: string) {
  return FEATURE_LABEL[code] ?? code
}

function fmtNum(n: number) {
  return n.toLocaleString('ko-KR')
}

// ─── 요약 카드 ───────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-4 flex flex-col gap-1">
      <p className="text-xs text-[var(--color-text-tertiary)]">{label}</p>
      <p className="text-xl font-bold text-[var(--color-text-primary)]">{value}</p>
      {sub && <p className="text-xs text-[var(--color-text-disabled)]">{sub}</p>}
    </div>
  )
}

// ─── 바 차트 (CSS 기반) ──────────────────────────────────────────────────────

function BarChart({ data }: { data: FeatureRow[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-[var(--color-text-tertiary)] text-center py-6">데이터 없음</p>
  }
  const max = Math.max(...data.map((r) => r.count), 1)
  return (
    <div className="space-y-2.5">
      {data.map((row) => (
        <div key={row.feature} className="flex items-center gap-3">
          <span className="text-xs text-[var(--color-text-secondary)] w-24 shrink-0 text-right">
            {featureLabel(row.feature)}
          </span>
          <div className="flex-1 bg-[var(--color-bg)] rounded-full h-4 overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--color-accent)] transition-all duration-500"
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-[var(--color-text-primary)] w-8 text-right">
            {fmtNum(row.count)}
          </span>
        </div>
      ))}
    </div>
  )
}

// ─── 라인 차트 (SVG 기반) ───────────────────────────────────────────────────

function LineChart({ data }: { data: DailyRow[] }) {
  if (data.length === 0) {
    return <p className="text-sm text-[var(--color-text-tertiary)] text-center py-6">데이터 없음</p>
  }
  const W = 600
  const H = 120
  const PAD = { top: 10, right: 10, bottom: 30, left: 36 }
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const maxV = Math.max(...data.map((r) => r.count), 1)
  const xScale = (i: number) => PAD.left + (i / Math.max(data.length - 1, 1)) * innerW
  const yScale = (v: number) => PAD.top + innerH - (v / maxV) * innerH

  const points = data.map((r, i) => `${xScale(i)},${yScale(r.count)}`).join(' ')

  // x축 라벨: 앞, 중간, 끝만 표시
  const labelIndices = [0, Math.floor(data.length / 2), data.length - 1].filter(
    (v, i, a) => a.indexOf(v) === i
  )

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 280 }}>
        {/* 그리드 */}
        {[0, 0.5, 1].map((ratio) => {
          const y = PAD.top + innerH * (1 - ratio)
          return (
            <g key={ratio}>
              <line
                x1={PAD.left} y1={y} x2={PAD.left + innerW} y2={y}
                stroke="var(--color-border-default)" strokeWidth={0.5}
              />
              <text
                x={PAD.left - 4} y={y + 4}
                textAnchor="end" fontSize={8}
                fill="var(--color-text-disabled)"
              >
                {Math.round(maxV * ratio)}
              </text>
            </g>
          )
        })}
        {/* 라인 */}
        <polyline
          points={points}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        {/* 점 */}
        {data.map((r, i) => (
          <circle key={i} cx={xScale(i)} cy={yScale(r.count)} r={2.5} fill="var(--color-accent)" />
        ))}
        {/* x축 라벨 */}
        {labelIndices.map((i) => (
          <text
            key={i}
            x={xScale(i)} y={H - 6}
            textAnchor="middle" fontSize={8}
            fill="var(--color-text-disabled)"
          >
            {data[i].date.slice(5)}
          </text>
        ))}
      </svg>
    </div>
  )
}

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────

export default function AdminUsage() {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()

  const [summary, setSummary] = useState<Summary | null>(null)
  const [features, setFeatures] = useState<FeatureRow[]>([])
  const [featureDays, setFeatureDays] = useState(7)
  const [users, setUsers] = useState<UserRow[]>([])
  const [userDays, setUserDays] = useState(7)
  const [daily, setDaily] = useState<DailyRow[]>([])
  const [dailyDays, setDailyDays] = useState(30)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login', { replace: true }); return }
    if (user && !user.is_admin) { navigate('/dashboard', { replace: true }); return }
    if (user) loadAll()
  }, [isAuthenticated, user])

  const loadAll = async () => {
    setLoading(true)
    try {
      const [sRes, fRes, uRes, dRes] = await Promise.all([
        client.get<Summary>('/admin/usage/summary'),
        client.get<FeaturesResp>(`/admin/usage/features?days=${featureDays}`),
        client.get<UsersResp>(`/admin/usage/users?days=${userDays}`),
        client.get<DailyResp>(`/admin/usage/daily?days=${dailyDays}`),
      ])
      setSummary(sRes.data)
      setFeatures(fRes.data.features)
      setUsers(uRes.data.users)
      setDaily(dRes.data.daily)
    } catch {
      toast('데이터를 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const reloadFeatures = async (days: number) => {
    setFeatureDays(days)
    try {
      const { data } = await client.get<FeaturesResp>(`/admin/usage/features?days=${days}`)
      setFeatures(data.features)
    } catch {
      toast('feature 데이터 로드 실패', 'error')
    }
  }

  const reloadUsers = async (days: number) => {
    setUserDays(days)
    try {
      const { data } = await client.get<UsersResp>(`/admin/usage/users?days=${days}`)
      setUsers(data.users)
    } catch {
      toast('사용자 데이터 로드 실패', 'error')
    }
  }

  const reloadDaily = async (days: number) => {
    setDailyDays(days)
    try {
      const { data } = await client.get<DailyResp>(`/admin/usage/daily?days=${days}`)
      setDaily(data.daily)
    } catch {
      toast('일별 데이터 로드 실패', 'error')
    }
  }

  const DaySelector = ({
    value,
    onChange,
    options = [7, 14, 30],
  }: {
    value: number
    onChange: (d: number) => void
    options?: number[]
  }) => (
    <div className="flex gap-1">
      {options.map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
            value === d
              ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)]'
              : 'bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-tertiary)] hover:border-[var(--color-border-accent)]'
          }`}
        >
          {d}일
        </button>
      ))}
    </div>
  )

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-5">

        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">사용량 대시보드</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">관리자 — API 호출 통계</p>
          </div>
          <div className="ml-auto">
            <Link
              to="/admin/inquiries"
              className="text-xs text-[var(--color-accent)] bg-[var(--color-accent)]/10 px-2.5 py-1 rounded-full font-medium hover:bg-[var(--color-accent)]/20 transition-colors"
            >
              문의 관리
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Spinner /></div>
        ) : (
          <>
            {/* ── 요약 카드 ── */}
            {summary && (
              <div>
                <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] mb-3">호출 요약</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <SummaryCard label="전체 호출" value={fmtNum(summary.total_calls)} />
                  <SummaryCard label="오늘 호출" value={fmtNum(summary.today_calls)} />
                  <SummaryCard label="최근 7일" value={fmtNum(summary.last_7d_calls)} />
                  <SummaryCard label="최근 30일" value={fmtNum(summary.last_30d_calls)} />
                  <SummaryCard
                    label="누적 토큰"
                    value={fmtNum(summary.total_openai_tokens)}
                    sub="OpenAI 전체"
                  />
                  <SummaryCard
                    label="30일 토큰"
                    value={fmtNum(summary.last_30d_openai_tokens)}
                    sub="OpenAI 최근 30일"
                  />
                </div>
              </div>
            )}

            {/* ── 기능별 호출 ── */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">기능별 호출 수</h2>
                <DaySelector value={featureDays} onChange={reloadFeatures} />
              </div>
              <BarChart data={features} />
            </div>

            {/* ── 일별 호출 ── */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">일별 호출 추이</h2>
                <DaySelector value={dailyDays} onChange={reloadDaily} options={[14, 30, 60]} />
              </div>
              <LineChart data={daily} />
            </div>

            {/* ── 상위 사용자 ── */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">상위 사용자</h2>
                <DaySelector value={userDays} onChange={reloadUsers} />
              </div>
              {users.length === 0 ? (
                <p className="text-sm text-[var(--color-text-tertiary)] text-center py-6">데이터 없음</p>
              ) : (
                <div className="space-y-1">
                  <div className="flex text-xs text-[var(--color-text-disabled)] px-1 mb-2">
                    <span className="w-6 text-center">#</span>
                    <span className="flex-1 ml-3">User ID</span>
                    <span className="w-16 text-right">호출 수</span>
                  </div>
                  {users.map((row, idx) => (
                    <div
                      key={row.user_id}
                      className="flex items-center px-1 py-2 rounded-xl hover:bg-[var(--color-bg)] transition-colors"
                    >
                      <span className="w-6 text-center text-xs font-bold text-[var(--color-text-disabled)]">
                        {idx + 1}
                      </span>
                      <span className="flex-1 ml-3 text-sm text-[var(--color-text-primary)]">
                        UID {row.user_id}
                      </span>
                      <span className="w-16 text-right text-sm font-semibold text-[var(--color-accent)]">
                        {fmtNum(row.count)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
